import type { BotDifficulty, VocationId } from '@estandarte/engine';
import { VOCATIONS } from '@estandarte/engine';
import { crestOf, portraitOf } from '../art';
import { CHARACTERS, guildHex, territoryName, theme } from '../theme';
import { Icon } from '../ui/Icon';

export interface SeatDraft {
  id: string;
  name: string;
  kind: 'human' | 'bot';
  color: string;
  vocation: VocationId;
  difficulty: BotDifficulty;
}

interface Props {
  index: number;
  seat: SeatDraft;
  takenColors: string[];
  editable: { name?: boolean; kind?: boolean; color?: boolean; vocation?: boolean; difficulty?: boolean };
  tags?: React.ReactNode;
  onChange(patch: Partial<SeatDraft>): void;
  onRemove?: () => void;
  removeLabel?: string;
  showVocation: boolean;
}

/** Uma linha de lugar: usada na nova partida local e no lobby online. */
export function SeatRow({ index, seat, takenColors, editable, tags, onChange, onRemove, removeLabel, showVocation }: Props) {
  return (
    <div className="seat" style={{ ['--seat-color' as string]: guildHex(seat.color) }}>
      <span className="idx">{index + 1}</span>
      {editable.name ? (
        <input
          className="input"
          value={seat.name}
          maxLength={20}
          onChange={(e) => onChange({ name: e.target.value })}
          aria-label={`Nome do lugar ${index + 1}`}
        />
      ) : (
        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{seat.name}</strong>
      )}
      {editable.kind ? (
        <select
          className="input"
          value={seat.kind === 'bot' ? `bot-${seat.difficulty}` : 'human'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'human') onChange({ kind: 'human' });
            else onChange({ kind: 'bot', difficulty: v === 'bot-easy' ? 'easy' : 'normal' });
          }}
          aria-label={`Tipo do lugar ${index + 1}`}
        >
          <option value="human">Humano</option>
          <option value="bot-normal">Bot normal</option>
          <option value="bot-easy">Bot fácil</option>
        </select>
      ) : editable.difficulty && seat.kind === 'bot' ? (
        <select
          className="input"
          value={seat.difficulty}
          onChange={(e) => onChange({ difficulty: e.target.value as BotDifficulty })}
          aria-label={`Dificuldade do lugar ${index + 1}`}
        >
          <option value="normal">Bot normal</option>
          <option value="easy">Bot fácil</option>
        </select>
      ) : (
        <span className="tag">{seat.kind === 'bot' ? `bot ${seat.difficulty === 'easy' ? 'fácil' : 'normal'}` : 'humano'}</span>
      )}
      <div className="row" style={{ gap: '0.35rem', flexWrap: 'nowrap' }}>
      <img src={crestOf(seat.color)} width={30} height={30} alt="" />
      <select
        className="input"
        style={{ flex: 1, minWidth: 0 }}
        value={seat.color}
        disabled={!editable.color}
        onChange={(e) => onChange({ color: e.target.value })}
        aria-label={`Guilda do lugar ${index + 1}`}
      >
        {theme.guilds.map((g) => (
          <option key={g.color} value={g.color} disabled={g.color !== seat.color && takenColors.includes(g.color)}>
            {g.name} ({g.colorName})
          </option>
        ))}
      </select>
      </div>
      {showVocation ? (
        <div className="vocation-pick" role="radiogroup" aria-label={`Vocação do lugar ${index + 1}`}>
          {VOCATIONS.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={seat.vocation === v}
              className={seat.vocation === v ? 'on' : ''}
              disabled={!editable.vocation && seat.vocation !== v}
              onClick={() => editable.vocation && onChange({ vocation: v as VocationId })}
              title={`${theme.vocations[v].name} — ${theme.vocations[v].role}: ${theme.vocations[v].text}`}
              style={!editable.vocation && seat.vocation !== v ? { opacity: 0.3 } : undefined}
            >
              <img src={portraitOf(v)} width={34} height={34} alt={theme.vocations[v].name} />
            </button>
          ))}
        </div>
      ) : (
        <span className="hint">sem vocações</span>
      )}
      <div className="seat-tags">
        {tags}
        {onRemove && (
          <button className="btn small ghost icon-btn" onClick={onRemove} aria-label={removeLabel ?? 'Remover'} title={removeLabel ?? 'Remover'}>
            <Icon name="close" />
          </button>
        )}
      </div>
      {seat.kind === 'bot' && <SeatBio name={seat.name.trim()} />}
    </div>
  );
}

export function AddonToggles({
  addons,
  onChange,
  disabled,
}: {
  addons: { sea: boolean; vocations: boolean; bosses: boolean };
  onChange?: (a: { sea: boolean; vocations: boolean; bosses: boolean }) => void;
  disabled?: boolean;
}) {
  return (
    <div className="addons">
      {(['sea', 'vocations', 'bosses'] as const).map((k) => (
        <label key={k} className="toggle" style={disabled ? { cursor: 'default' } : undefined}>
          <input
            type="checkbox"
            checked={addons[k]}
            disabled={disabled}
            onChange={(e) => onChange?.({ ...addons, [k]: e.target.checked })}
          />
          <div>
            <strong>{theme.strings.addons[k].name}</strong>
            <span>{theme.strings.addons[k].text}</span>
          </div>
        </label>
      ))}
    </div>
  );
}

/** Linha com o apelido, a cidade e a frase do personagem do bot. */
function SeatBio({ name }: { name: string }) {
  const ch = CHARACTERS[name];
  if (!ch) return null;
  const city = territoryName(ch.origin);
  return (
    <div className="seat-bio" title={ch.story}>
      <em>{ch.epithet}</em>
      {ch.epithet.includes(city) ? '' : `, de ${city}`}: “{ch.quote}”
    </div>
  );
}
